import { useEffect, useState } from "react";
import { Search, X, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api, errorText } from "@/lib/api";

export const EMPTY_FILTERS = { q: "", source: "", identity: "", operator: "", device: "", anchored: "", verification: "", direction: "", area: "", startDate: "", endDate: "" };

export const toParams = (f) => {
  const p = { q: f.q, source: f.source, identity: f.identity, operator: f.operator, device: f.device, anchored: f.anchored, verification: f.verification, direction: f.direction, area: f.area };
  if (f.startDate) p.start = new Date(`${f.startDate}T00:00:00`).toISOString();
  if (f.endDate) p.end = new Date(`${f.endDate}T23:59:59.999`).toISOString();
  return Object.fromEntries(Object.entries(p).filter(([, v]) => v));
};

const ALL = "__all";

const FilterSelect = ({ label, value, options, onChange, testId }) => (
  <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
    <SelectTrigger className="bg-[#0F141C] min-w-0" data-testid={testId}><SelectValue placeholder={label} /></SelectTrigger>
    <SelectContent>
      <SelectItem value={ALL} data-testid={`${testId}-all`}>{label}</SelectItem>
      {options.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`${testId}-opt-${v}`}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const LedgerFilters = ({ filters, onChange, tick, total }) => {
  const [facets, setFacets] = useState({ identity: [], operator: [], device: [], area: [] });
  const [exporting, setExporting] = useState("");
  const set = (k) => (v) => onChange({ ...filters, [k]: v });
  const active = Object.values(filters).some(Boolean);

  useEffect(() => { api.get("/entries/facets").then((r) => setFacets(r.data)).catch(() => {}); }, [tick]);

  const exportAs = async (format) => {
    setExporting(format);
    try {
      const res = await api.get("/entries/export", { params: { ...toParams(filters), format }, responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || `doorman-ledger.${format}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${Math.min(total, 100000).toLocaleString()} arrivals as ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setExporting("");
    }
  };

  const opts = (arr) => arr.map((v) => [v, v]);

  return (
    <div className="rounded-md border border-[#222D3D] bg-[#141A24] p-4 space-y-3" data-testid="ledger-filters">
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input placeholder="Search agent, company, device, page, user-agent or hash" value={filters.q} onChange={(e) => set("q")(e.target.value)} className="pl-9 bg-[#0F141C]" data-testid="ledger-search-input" />
        </div>
        <div className="flex gap-2">
          {active && (
            <Button variant="ghost" onClick={() => onChange(EMPTY_FILTERS)} className="text-slate-400" data-testid="ledger-clear-filters-btn"><X className="h-4 w-4 mr-1.5" />Clear</Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="border-[#222D3D] bg-transparent" disabled={!total || !!exporting} data-testid="ledger-export-btn">
                {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#0F141C] border-[#222D3D]">
              <DropdownMenuItem onClick={() => exportAs("csv")} data-testid="ledger-export-csv">CSV (spreadsheet)</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportAs("json")} data-testid="ledger-export-json">JSON (with hashes)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
        <FilterSelect label="All directions" value={filters.direction} options={[["outbound", "Outbound (used inside)"], ["inbound", "Inbound (reaching in)"], ["web", "Website visits"]]} onChange={set("direction")} testId="ledger-filter-direction" />
        <FilterSelect label="All network areas" value={filters.area} options={opts(facets.area || [])} onChange={set("area")} testId="ledger-filter-area" />
        <FilterSelect label="All agents" value={filters.identity} options={opts(facets.identity)} onChange={set("identity")} testId="ledger-filter-agent" />
        <FilterSelect label="All companies" value={filters.operator} options={opts(facets.operator)} onChange={set("operator")} testId="ledger-filter-operator" />
        <FilterSelect label="All devices" value={filters.device} options={opts(facets.device)} onChange={set("device")} testId="ledger-filter-device" />
        <FilterSelect label="All sources" value={filters.source} options={[["auto", "Detected"], ["manual", "By hand"]]} onChange={set("source")} testId="ledger-filter-source" />
        <FilterSelect label="Bitcoin: any" value={filters.anchored} options={[["yes", "Anchored"], ["no", "Waiting"]]} onChange={set("anchored")} testId="ledger-filter-anchored" />
        <FilterSelect label="Origin: any" value={filters.verification} options={[["verified", "Verified"], ["failed", "Impersonator"], ["undeclared", "Undeclared"], ["unverifiable", "Unverified"]]} onChange={set("verification")} testId="ledger-filter-verification" />
        <Input type="date" value={filters.startDate} max={filters.endDate || undefined} onChange={(e) => set("startDate")(e.target.value)} className="bg-[#0F141C] [color-scheme:dark]" aria-label="From date" data-testid="ledger-filter-start-date" />
        <Input type="date" value={filters.endDate} min={filters.startDate || undefined} onChange={(e) => set("endDate")(e.target.value)} className="bg-[#0F141C] [color-scheme:dark]" aria-label="To date" data-testid="ledger-filter-end-date" />
      </div>
      <p className="text-xs text-slate-500">Export includes every matching arrival (up to 100,000), oldest first, with entry and previous hashes so the chain can be checked independently.</p>
    </div>
  );
};
